/* eslint-disable no-underscore-dangle */
/**
 * Assign a driver and a car to one booking — and hand the trip to the driver
 * without leaving the dialog.
 *
 * The dialog reads top to bottom as the job is actually done:
 *   1. what the trip is        — enough to assign without opening the booking
 *   2. who drives it           — driver, car, and whether they can sign in
 *   3. what Save will send     — spelled out, and it follows the toggles
 *   4. hand it to the driver   — one message on the clipboard
 *
 * What Save posts has not changed: the same `bookings/assign-driver-and-car`
 * body as before, field for field.
 */
import React, { useEffect, useState, useRef } from 'react';
import {
  Modal, Form, Button, Alert, Spinner, Badge, ListGroup, ButtonGroup,
} from 'react-bootstrap';
import _ from 'lodash';
import { Typeahead } from 'react-bootstrap-typeahead';
import Error from '../../../Components/Error';
import FormControlFeedback from '../../../Components/FormControlFeedback';
import BookingService from '../../../Services/BookingService';
import setStateFromInput from '../../../Utils/setStateFromInput';
import OperatorService from '../../../Services/OperatorService';
import PortalUsersService, { PORTAL_USER_KINDS } from '../../../Services/PortalUsersService';
import PortalAccessHandover from '../../Operators/Components/PortalAccessHandover';
import copyText from '../../Portal/copyText';
import { composeDriverName, driverNameErrors, hasDriverName } from '../../../Utils/driverName';
import { formatBookingField, DATE_FORMATS } from '../../../Utils/timezone';
import { operatorRoleBadge } from '../../../config/bookingConstants';
import {
  LANGUAGES, languageOptions, defaultLanguage, driverMessage, carLabel, driverLabel,
  passengerCount, portalStateFor, PORTAL_STATES, PORTAL_STATE_LABELS, canUsePortal,
  saveEffects, saveLabel, emailListProblem, partnerAssignmentQueue,
} from './driverHandover';

// The dialog is one thing at a time: picking, or adding, or handing over.
const STEPS = {
  ASSIGN: 'assign', ADD_DRIVER: 'add-driver', ADD_CAR: 'add-car', HANDOVER: 'handover',
};

const idOf = (row) => row?.Id || row?.id || row?._id || '';
const isActive = (row) => row?.active !== 0 && row?.active !== false;

/** A list fetch as the sections read it: idle → loading → ready | error. */
const emptyList = { rows: [], status: 'idle', error: '' };

function DriverInputModal({
  originalOrder: initialOrder = {},
  show = false,
  handleClose,
  fetchOrders,
  // For experience bookings: which operator in operators[] array
  operatorIndex: propOperatorIndex = null,
}) {
  const [originalOrder, setOriginalOrder] = useState(initialOrder);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const fetchedRef = useRef(false);

  // Check if this is a booking with multi-operator (has operators array with driver)
  const hasMultiOperator = originalOrder.operators?.length > 0;

  // Auto-find the driver operator index if not provided
  const operatorIndex = propOperatorIndex !== null
    ? propOperatorIndex
    : originalOrder.operators?.findIndex((op) => op.role === 'driver') ?? null;

  const selectedOperator = hasMultiOperator && operatorIndex !== null && operatorIndex >= 0
    ? originalOrder.operators?.[operatorIndex]
    : null;

  // Determine which operator_id to use for fetching drivers/cars
  const effectiveOperatorId = selectedOperator?.operator_id || originalOrder.operator_id;
  const operatorName = selectedOperator?.operator?.name || originalOrder.operator?.name || '';

  const defaultsFor = (source) => ({
    operator_driver_id: '',
    operator_car_id: '',
    notify_email: source.email_address ?? false,
    notify_whatsapp: true,
    send_driver_phone_number: true,
  });

  const defaultOrder = defaultsFor(originalOrder);

  // `_.defaults({}, …)` and not `_.defaults(originalOrder, …)`: the second form
  // wrote this form's defaults into the orders table's own row object.
  const [order, setOrder] = useState(() => _.defaults({}, originalOrder, defaultOrder));

  const defaultDriver = { operator_id: effectiveOperatorId, active: 1 };
  const defaultCar = { operator_id: effectiveOperatorId, active: 1 };

  const [step, setStep] = useState(STEPS.ASSIGN);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [drivers, setDrivers] = useState(emptyList);
  const [cars, setCars] = useState(emptyList);
  const [driver, setDriver] = useState(_.defaults({}, defaultDriver));
  const [car, setCar] = useState(_.defaults({}, defaultCar));
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);

  const [selectedDriver, setSelectedDriver] = useState(
    originalOrder.driver ? [originalOrder.driver] : [],
  );
  const [selectedCar, setSelectedCar] = useState([]);

  // Portal logins for this operator — fetched the first time a driver is
  // picked, because that is the first moment the answer means anything.
  const [portalUsers, setPortalUsers] = useState(emptyList);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [handover, setHandover] = useState(null);

  const [language, setLanguage] = useState(LANGUAGES.EN);
  const [languageTouched, setLanguageTouched] = useState(false);
  const [copied, setCopied] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const messageRef = useRef(null);

  const chosenDriver = selectedDriver[0] || null;
  const chosenCar = selectedCar[0] || null;
  const portal = portalStateFor(portalUsers.rows, idOf(chosenDriver));
  const portalLabel = PORTAL_STATE_LABELS[portal.state];

  const handleChange = (e) => setStateFromInput(e, setOrder);

  /* --- fetching --------------------------------------------------------- */

  const fetchDrivers = async () => {
    if (!effectiveOperatorId) return;
    setDrivers((prev) => ({ ...prev, status: 'loading', error: '' }));
    try {
      const response = await OperatorService.fetchDrivers(effectiveOperatorId);
      setDrivers({ rows: response?.data?.data || [], status: 'ready', error: '' });
    } catch (err) {
      setDrivers({ rows: [], status: 'error', error: 'Could not load this operator\'s drivers.' });
    }
  };

  const fetchCars = async () => {
    if (!effectiveOperatorId) return;
    setCars((prev) => ({ ...prev, status: 'loading', error: '' }));
    try {
      const response = await OperatorService.fetchCars(effectiveOperatorId);
      setCars({ rows: response?.data?.data || [], status: 'ready', error: '' });
    } catch (err) {
      setCars({ rows: [], status: 'error', error: 'Could not load this operator\'s cars.' });
    }
  };

  const fetchPortalUsers = async () => {
    if (!effectiveOperatorId) return;
    setPortalUsers((prev) => ({ ...prev, status: 'loading', error: '' }));
    try {
      setPortalUsers({
        rows: await PortalUsersService.list(effectiveOperatorId), status: 'ready', error: '',
      });
    } catch (err) {
      setPortalUsers({ rows: [], status: 'error', error: 'Could not check portal access.' });
    }
  };

  useEffect(() => {
    fetchDrivers();
    fetchCars();
  }, [effectiveOperatorId]);

  // Only once somebody has picked a driver: before that there is nothing to
  // say, and the operator's portal list is not free.
  useEffect(() => {
    if (chosenDriver && portalUsers.status === 'idle') fetchPortalUsers();
  }, [chosenDriver]);

  // Fetch fresh booking data when modal opens
  useEffect(() => {
    const fetchBooking = async () => {
      if (show && initialOrder?._id && !fetchedRef.current) {
        fetchedRef.current = true;
        setIsLoadingOrder(true);
        try {
          const freshOrder = await BookingService.getBookingById(initialOrder._id);
          if (freshOrder) {
            setOriginalOrder(freshOrder);
            setOrder(_.defaults({}, freshOrder, defaultsFor(freshOrder)));
            if (freshOrder.driver) setSelectedDriver([freshOrder.driver]);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Error fetching booking:', err);
        } finally {
          setIsLoadingOrder(false);
        }
      }
    };

    fetchBooking();
  }, [show, initialOrder?._id]);

  // Reset fetchedRef when modal closes
  useEffect(() => {
    if (!show) fetchedRef.current = false;
  }, [show]);

  // Keep the posted ids in step with what is picked.
  useEffect(() => {
    setOrder((prev) => ({
      ...prev,
      operator_driver_id: idOf(chosenDriver) || '',
      operator_car_id: idOf(chosenCar) || prev.operator_car_id || '',
    }));
  }, [chosenDriver, chosenCar]);

  // Re-attach the booking's saved driver/car once the lists arrive, so the
  // dialog opens showing what is already assigned.
  useEffect(() => {
    if (chosenDriver || !order.driver) return;
    const match = drivers.rows.find((row) => idOf(row) === idOf(order.driver));
    if (match) setSelectedDriver([match]);
  }, [drivers.rows]);

  useEffect(() => {
    if (chosenCar || !order.operator_car_id) return;
    const match = cars.rows.find((row) => idOf(row) === order.operator_car_id);
    if (match) setSelectedCar([match]);
  }, [cars.rows, order.operator_car_id]);

  // The language follows the driver until staff pick one themselves.
  useEffect(() => {
    if (!languageTouched) setLanguage(defaultLanguage(chosenDriver));
  }, [chosenDriver, languageTouched]);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(''), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  /* --- actions ---------------------------------------------------------- */

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    setValidationErrors([]);

    try {
      const data = {
        operator_driver_id: idOf(chosenDriver) || '',
        operator_car_id: idOf(chosenCar) || '',
        _id: order._id,
        notify_email: order.notify_email,
        notify_whatsapp: order.notify_whatsapp,
        send_driver_phone_number: order.send_driver_phone_number,
      };

      // For multi-operator bookings, include operator index
      if (hasMultiOperator && operatorIndex !== null && operatorIndex >= 0) {
        data.operator_index = operatorIndex;
      }

      await BookingService.assignDriverAndCar(data);
      if (fetchOrders) fetchOrders();
      handleClose();
    } catch (err) {
      // Nothing the user typed is thrown away: the dialog stays exactly as it
      // was, with the reason on top of it.
      setError(err.response?.data?.message || 'Could not assign the driver and car. Nothing was sent.');
      if (err.response?.data?.errors) setValidationErrors(err.response.data.errors);
    } finally {
      setSaving(false);
    }
  };

  const openStep = (next) => {
    setValidationErrors([]);
    setStep(next);
  };

  const backToAssign = () => {
    setValidationErrors([]);
    setStep(STEPS.ASSIGN);
  };

  const handleAddDriver = async () => {
    // A driver with half a name is what Booking.com refuses, and the
    // refusal arrives hours later with the trip already sold — so it is
    // said here, before the round trip, in the same place a 400 would say
    // it.
    const missing = driverNameErrors(driver);

    if (missing.length) {
      setValidationErrors(missing);
      return;
    }

    setAdding(true);
    setValidationErrors([]);
    try {
      const response = await OperatorService.addDriver(driver);
      const created = response?.data?.data;
      const fresh = await OperatorService.fetchDrivers(effectiveOperatorId);
      const rows = fresh?.data?.data || [];
      setDrivers({ rows, status: 'ready', error: '' });
      // Come back to the selection with the new driver already chosen.
      // `full_name` is composed by the API from the two halves, so the
      // fallback match composes the same string rather than looking for a
      // field this form no longer sends.
      const match = rows.find((row) => idOf(row) === idOf(created))
        || rows.find((row) => row.full_name === composeDriverName(driver)
          && row.phone_number === driver.phone_number);
      if (match) setSelectedDriver([match]);
      setDriver(_.defaults({}, defaultDriver));
      setStep(STEPS.ASSIGN);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not add the driver.');
      if (err.response?.data?.errors) setValidationErrors(err.response.data.errors);
    } finally {
      setAdding(false);
    }
  };

  const handleAddCar = async () => {
    setAdding(true);
    setValidationErrors([]);
    try {
      const response = await OperatorService.addCar(car);
      const created = response?.data?.data;
      const fresh = await OperatorService.fetchCars(effectiveOperatorId);
      const rows = fresh?.data?.data || [];
      setCars({ rows, status: 'ready', error: '' });
      const match = rows.find((row) => idOf(row) === idOf(created))
        || rows.find((row) => row.plate_number === car.plate_number);
      if (match) setSelectedCar([match]);
      setCar(_.defaults({}, defaultCar));
      setStep(STEPS.ASSIGN);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not add the car.');
      if (err.response?.data?.errors) setValidationErrors(err.response.data.errors);
    } finally {
      setAdding(false);
    }
  };

  const handleInvite = async () => {
    setInviting(true);
    setValidationErrors([]);
    try {
      const grant = await PortalUsersService.grant({
        operatorId: effectiveOperatorId,
        kind: PORTAL_USER_KINDS.DRIVER,
        driverId: idOf(chosenDriver),
        email: inviteEmail.trim(),
      });
      setHandover({
        token: grant.token,
        expiresAt: grant.expiresAt,
        user: {
          ...grant.user,
          name: chosenDriver?.full_name,
          phone: chosenDriver?.phone_number,
        },
      });
      setInviteEmail('');
      setStep(STEPS.HANDOVER);
      await fetchPortalUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not invite this driver to the portal.');
      if (err.response?.data?.errors) setValidationErrors(err.response.data.errors);
    } finally {
      setInviting(false);
    }
  };

  const message = driverMessage({
    order,
    driver: chosenDriver,
    car: chosenCar,
    language,
    hasPortal: canUsePortal(portal.state),
  });

  // The portal's own clipboard helper (the Clipboard API, then the old
  // select-and-copy). When both are refused, show the text and select it so
  // the only thing left is Ctrl/Cmd+C.
  const handleCopyMessage = async () => {
    if (await copyText(message)) {
      setCopied('ok');
      return;
    }
    setShowMessage(true);
    setCopied('manual');
    window.setTimeout(() => messageRef.current?.select(), 0);
  };

  /* --- derived reading -------------------------------------------------- */

  const roleInfo = selectedOperator?.role
    ? operatorRoleBadge(selectedOperator.role, selectedOperator.operator)
    : null;
  const effects = saveEffects(order, { driver: chosenDriver });
  const emailProblem = emailListProblem(order.notify_email);
  const activeDrivers = drivers.rows.filter(isActive);
  const activeCars = cars.rows.filter(isActive);
  const pax = passengerCount(order);
  const title = {
    [STEPS.ADD_DRIVER]: 'Add a driver',
    [STEPS.ADD_CAR]: 'Add a car',
    [STEPS.HANDOVER]: 'Portal access',
  }[step] || 'Assign driver & car';

  const renderListState = (list, emptyText, what) => {
    if (list.status === 'loading') {
      return (
        <div className="form-text d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" />
          {`Loading ${what}…`}
        </div>
      );
    }
    if (list.status === 'error') {
      return (
        <div className="form-text text-danger d-flex align-items-center gap-2">
          <span>{list.error}</span>
          <Button variant="link" size="sm" className="p-0" onClick={what === 'drivers' ? fetchDrivers : fetchCars}>
            Try again
          </Button>
        </div>
      );
    }
    if (list.status === 'ready' && !list.rows.length) {
      return <div className="form-text">{emptyText}</div>;
    }
    return null;
  };

  /* --- the steps -------------------------------------------------------- */

  const addDriverStep = (
    <>
      <p className="text-muted small">
        {`A new driver for ${operatorName || 'this operator'}. They are selected for this booking as soon as they are saved.`}
      </p>
      <div className="row">
        <Form.Group className="mb-3 col-12 col-md-4" controlId="first_name">
          <Form.Label>First name *</Form.Label>
          <Form.Control
            value={driver.first_name || ''}
            placeholder="Péter"
            autoComplete="off"
            onChange={(e) => setStateFromInput(e, setDriver)}
          />
          <FormControlFeedback errors={validationErrors} field="first_name" />
        </Form.Group>

        <Form.Group className="mb-3 col-12 col-md-4" controlId="last_name">
          <Form.Label>Last name *</Form.Label>
          <Form.Control
            value={driver.last_name || ''}
            placeholder="Kovács"
            autoComplete="off"
            onChange={(e) => setStateFromInput(e, setDriver)}
          />
          <FormControlFeedback errors={validationErrors} field="last_name" />
        </Form.Group>

        <Form.Group className="mb-3 col-12 col-md-4" controlId="phone_number">
          <Form.Label>Phone number</Form.Label>
          <Form.Control
            value={driver.phone_number || ''}
            placeholder="+36 30 123 4567"
            autoComplete="off"
            onChange={(e) => setStateFromInput(e, setDriver)}
          />
          <FormControlFeedback errors={validationErrors} field="phone_number" />
        </Form.Group>
      </div>
    </>
  );

  const addCarStep = (
    <>
      <p className="text-muted small">
        {`A new car for ${operatorName || 'this operator'}. It is selected for this booking as soon as it is saved.`}
      </p>
      <div className="row">
        <Form.Group className="mb-3 col-12 col-md-4" controlId="type">
          <Form.Label>Type</Form.Label>
          <Form.Control
            placeholder="Skoda Superb"
            value={car.type || ''}
            onChange={(e) => setStateFromInput(e, setCar)}
          />
          <FormControlFeedback errors={validationErrors} field="type" />
        </Form.Group>

        <Form.Group className="mb-3 col-12 col-md-4" controlId="color">
          <Form.Label>Colour</Form.Label>
          <Form.Control
            placeholder="White"
            value={car.color || ''}
            onChange={(e) => setStateFromInput(e, setCar)}
          />
          <FormControlFeedback errors={validationErrors} field="color" />
        </Form.Group>

        <Form.Group className="mb-3 col-12 col-md-4" controlId="plate_number">
          <Form.Label>Plate number</Form.Label>
          <Form.Control
            placeholder="WWW-111"
            value={car.plate_number || ''}
            onChange={(e) => setStateFromInput(e, setCar)}
          />
          <FormControlFeedback errors={validationErrors} field="plate_number" />
        </Form.Group>
      </div>
    </>
  );

  const assignStep = (
    <>
      {partnerAssignmentQueue(order.partner) && (
        <Alert variant="info" className="py-2 px-3 small d-flex align-items-start gap-2">
          <i className="fa fa-plug mt-1" />
          <span>
            {`${order.partner?.name || 'This partner'} takes driver details automatically — saving pushes the driver and car to them.`}
          </span>
        </Alert>
      )}

      {/* 1 — what the trip is */}
      <section className="assign-section" aria-labelledby="assign-trip-heading">
        <h6 className="assign-section-title" id="assign-trip-heading">The trip</h6>
        <div className="assign-trip">
          <div className="assign-trip-when">
            <span className="assign-trip-time">
              {formatBookingField(order, 'date_datetime', DATE_FORMATS.TIME_ONLY) || '—'}
            </span>
            <span className="assign-trip-date">
              {formatBookingField(order, 'date_datetime', DATE_FORMATS.DATE_ONLY)}
            </span>
            <span className="assign-trip-tz">Budapest time</span>
          </div>

          <ul className="assign-trip-route">
            <li>
              <i className="fa fa-map-marker-alt text-success" aria-hidden="true" />
              <span className="assign-trip-leg">Pickup</span>
              <span>{order.destination_from?.address || '—'}</span>
            </li>
            <li>
              <i className="fa fa-flag-checkered text-danger" aria-hidden="true" />
              <span className="assign-trip-leg">Drop-off</span>
              <span>{order.destination_to?.address || '—'}</span>
            </li>
          </ul>

          <ul className="assign-trip-facts">
            <li>
              <i className="fa fa-user me-1" aria-hidden="true" />
              {order.full_name || 'Passenger'}
            </li>
            <li>
              <i className="fa fa-users me-1" aria-hidden="true" />
              {`${pax} ${pax === 1 ? 'passenger' : 'passengers'}`}
            </li>
            {order.flight_number && (
              <li>
                <i className="fa fa-plane me-1" aria-hidden="true" />
                {order.flight_number}
              </li>
            )}
            <li className="assign-trip-ref">
              <i className="fa fa-hashtag me-1" aria-hidden="true" />
              {order.booking_reference || '—'}
            </li>
          </ul>
        </div>
      </section>

      {/* 2 — who drives it */}
      <section className="assign-section" aria-labelledby="assign-who-heading">
        <h6 className="assign-section-title" id="assign-who-heading">Who drives it</h6>

        <div className="row">
          <Form.Group className="mb-3 col-12 col-md-6">
            <Form.Label htmlFor="assign-driver-input">Driver</Form.Label>
            <Typeahead
              id="assign-driver"
              inputProps={{ id: 'assign-driver-input' }}
              placeholder="Search by name or phone…"
              options={activeDrivers}
              labelKey={driverLabel}
              selected={selectedDriver}
              onChange={setSelectedDriver}
              disabled={drivers.status === 'loading'}
              emptyLabel="No driver matches that."
              renderMenuItemChildren={(option) => (
                <>
                  <div className="fw-semibold">{option.full_name}</div>
                  <div className="small text-muted">{option.phone_number || 'No phone number'}</div>
                </>
              )}
            />
            {renderListState(
              drivers,
              `${operatorName || 'This operator'} has no drivers yet — add the first one.`,
              'drivers',
            )}
            <FormControlFeedback errors={validationErrors} field="operator_driver_id" />
            <Button
              variant="link"
              size="sm"
              className="p-0 mt-1"
              onClick={() => openStep(STEPS.ADD_DRIVER)}
            >
              <i className="fa fa-plus me-1" aria-hidden="true" />
              Add a driver
            </Button>
          </Form.Group>

          <Form.Group className="mb-3 col-12 col-md-6">
            <Form.Label htmlFor="assign-car-input">Car</Form.Label>
            <Typeahead
              id="assign-car"
              inputProps={{ id: 'assign-car-input' }}
              placeholder="Search by plate, type or colour…"
              options={activeCars}
              labelKey={carLabel}
              selected={selectedCar}
              onChange={setSelectedCar}
              disabled={cars.status === 'loading'}
              emptyLabel="No car matches that."
              renderMenuItemChildren={(option) => (
                <>
                  <div className="fw-semibold">{option.plate_number || 'No plate'}</div>
                  <div className="small text-muted">
                    {[option.type, option.color].filter(Boolean).join(' · ') || 'No details'}
                  </div>
                </>
              )}
            />
            {renderListState(
              cars,
              `${operatorName || 'This operator'} has no cars yet — add the first one.`,
              'cars',
            )}
            <FormControlFeedback errors={validationErrors} field="operator_car_id" />
            <Button
              variant="link"
              size="sm"
              className="p-0 mt-1"
              onClick={() => openStep(STEPS.ADD_CAR)}
            >
              <i className="fa fa-plus me-1" aria-hidden="true" />
              Add a car
            </Button>
          </Form.Group>
        </div>

        {chosenDriver && (
          <div className="assign-portal" data-testid="portal-status">
            {portalUsers.status === 'loading' && (
              <span className="small text-muted d-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" />
                Checking portal access…
              </span>
            )}

            {portalUsers.status === 'error' && (
              <span className="small text-danger d-flex align-items-center gap-2">
                {portalUsers.error}
                <Button variant="link" size="sm" className="p-0" onClick={fetchPortalUsers}>
                  Try again
                </Button>
              </span>
            )}

            {portalUsers.status === 'ready' && (
              <div className="d-flex flex-wrap align-items-center gap-2">
                <Badge bg={portalLabel.variant} className="d-inline-flex align-items-center gap-1">
                  <i className={`fa ${portalLabel.icon}`} aria-hidden="true" />
                  {portalLabel.text}
                </Badge>
                {portal.user?.email && (
                  <span className="small text-muted">{portal.user.email}</span>
                )}
                {portal.state === PORTAL_STATES.NONE && (
                  <span className="small text-muted">
                    They cannot open the portal, so the message below carries the whole trip.
                  </span>
                )}
              </div>
            )}

            {portalUsers.status === 'ready' && portal.state === PORTAL_STATES.NONE && (
              <div className="row g-2 align-items-end mt-2">
                <Form.Group className="col-12 col-sm-7" controlId="portal_invite_email">
                  <Form.Label className="small mb-1">
                    Email for the portal login
                    <span className="text-muted fw-normal"> — a driver record has no address on file</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    value={inviteEmail}
                    placeholder="driver@example.com"
                    autoComplete="off"
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                  <FormControlFeedback errors={validationErrors} field="email" />
                </Form.Group>
                <div className="col-12 col-sm-5 d-grid">
                  <Button
                    variant="outline-primary"
                    onClick={handleInvite}
                    disabled={inviting || !inviteEmail.trim()}
                  >
                    {inviting ? 'Inviting…' : 'Invite to portal'}
                  </Button>
                </div>
              </div>
            )}

            {portalUsers.status === 'ready' && portal.state === PORTAL_STATES.REVOKED && (
              <div className="form-text">
                Access was taken away. Restore it from the operator&apos;s Portal users screen.
              </div>
            )}
          </div>
        )}
      </section>

      {/* 3 — what Save will send */}
      <section className="assign-section" aria-labelledby="assign-send-heading">
        <h6 className="assign-section-title" id="assign-send-heading">What Save will send</h6>

        <Form.Group className="mb-3" controlId="notify_email">
          <Form.Label>Notify these email addresses</Form.Label>
          <Form.Control
            placeholder="customer@domain.com, customer2@gmail.com"
            type="text"
            value={order.notify_email || ''}
            onChange={handleChange}
          />
          {emailProblem ? (
            <div className="form-text text-danger" role="alert">{emailProblem}</div>
          ) : (
            <div className="form-text">Leave it empty to assign without emailing anyone.</div>
          )}
          <FormControlFeedback errors={validationErrors} field="notify_email" />
        </Form.Group>

        <Form.Group controlId="send_driver_phone_number" className="mb-2">
          <Form.Check
            type="switch"
            checked={!!order.send_driver_phone_number}
            onChange={handleChange}
            label="Share the driver's phone number with the customer"
          />
          <FormControlFeedback errors={validationErrors} field="send_driver_phone_number" />
        </Form.Group>

        <ListGroup variant="flush" className="assign-effects border rounded" data-testid="save-effects">
          {effects.length ? effects.map((effect) => (
            <ListGroup.Item key={effect.text} className="d-flex align-items-start gap-2 px-3 py-2 small">
              <i className={`${effect.icon === 'fa-whatsapp' ? 'fab' : 'fa'} ${effect.icon} mt-1 text-muted`} aria-hidden="true" />
              <span>{effect.text}</span>
            </ListGroup.Item>
          )) : (
            <ListGroup.Item className="px-3 py-2 small text-muted">
              Nothing is sent — the booking is only updated.
            </ListGroup.Item>
          )}
        </ListGroup>
      </section>

      {/* 4 — hand it to the driver */}
      <section className="assign-section" aria-labelledby="assign-handover-heading">
        <h6 className="assign-section-title" id="assign-handover-heading">Hand it to the driver</h6>

        {!chosenDriver ? (
          <p className="text-muted small mb-0">Pick a driver to get their message.</p>
        ) : (
          <>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <Button variant="outline-primary" onClick={handleCopyMessage}>
                <i className={`fa ${copied === 'ok' ? 'fa-check' : 'fa-copy'} me-2`} aria-hidden="true" />
                {copied === 'ok' ? 'Copied' : 'Copy message for the driver'}
              </Button>

              <ButtonGroup size="sm" aria-label="Message language">
                {languageOptions.map((option) => (
                  <Button
                    key={option.value}
                    variant={language === option.value ? 'secondary' : 'outline-secondary'}
                    active={language === option.value}
                    onClick={() => { setLanguage(option.value); setLanguageTouched(true); }}
                  >
                    {option.label}
                  </Button>
                ))}
              </ButtonGroup>

              <Button
                variant="link"
                size="sm"
                className="p-0"
                onClick={() => setShowMessage((prev) => !prev)}
                aria-expanded={showMessage}
                aria-controls="assign-message-preview"
              >
                {showMessage ? 'Hide the message' : 'Show the message'}
              </Button>
            </div>

            <div aria-live="polite" className="small">
              {copied === 'ok' && <span className="text-success">Copied — paste it into WhatsApp.</span>}
              {copied === 'manual' && (
                <span className="text-warning-emphasis">
                  This browser would not let us reach the clipboard. The message is
                  selected below — copy it by hand.
                </span>
              )}
            </div>

            {showMessage && (
              <Form.Group className="mt-2" controlId="assign-message-preview">
                <Form.Label className="visually-hidden">Message for the driver</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={Math.min(12, message.split('\n').length + 1)}
                  readOnly
                  value={message}
                  ref={messageRef}
                />
              </Form.Group>
            )}

            <div className="form-text">
              {canUsePortal(portal.state)
                ? 'Short version: they have the portal, and a forwarded message should not carry the customer\'s details.'
                : 'Includes their event link for "on my way" and "arrived".'}
              {' The customer\'s phone number is never in it.'}
            </div>
          </>
        )}
      </section>
    </>
  );

  const stepBody = {
    [STEPS.ADD_DRIVER]: addDriverStep,
    [STEPS.ADD_CAR]: addCarStep,
    [STEPS.HANDOVER]: handover ? (
      <PortalAccessHandover
        user={handover.user}
        token={handover.token}
        expiresAt={handover.expiresAt}
        operatorName={operatorName}
      />
    ) : null,
  }[step] || assignStep;

  const footer = {
    [STEPS.ADD_DRIVER]: (
      <>
        <Button variant="outline-secondary" onClick={backToAssign} disabled={adding}>Cancel</Button>
        <Button onClick={handleAddDriver} disabled={adding || !hasDriverName(driver)}>
          {adding ? 'Adding…' : 'Add & select'}
        </Button>
      </>
    ),
    [STEPS.ADD_CAR]: (
      <>
        <Button variant="outline-secondary" onClick={backToAssign} disabled={adding}>Cancel</Button>
        <Button onClick={handleAddCar} disabled={adding || !String(car.plate_number || '').trim()}>
          {adding ? 'Adding…' : 'Add & select'}
        </Button>
      </>
    ),
    [STEPS.HANDOVER]: (
      <Button onClick={backToAssign}>Back to the assignment</Button>
    ),
  }[step] || (
    <>
      <Button variant="outline-secondary" onClick={handleClose} disabled={saving}>Cancel</Button>
      <Button onClick={handleSubmit} disabled={saving}>
        {saving ? (
          <>
            <Spinner animation="border" size="sm" className="me-2" />
            Saving…
          </>
        ) : saveLabel(order)}
      </Button>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      className="mobile-modal assign-modal"
      aria-labelledby="assign-modal-title"
    >
      <Modal.Header closeButton>
        <Modal.Title as="div">
          <div className="h5 mb-0" id="assign-modal-title">
            {title}
            {selectedOperator && roleInfo && (
              <Badge bg={roleInfo.color} className="ms-2">
                <i className={`fa ${roleInfo.icon} me-1`} aria-hidden="true" />
                {roleInfo.label}
              </Badge>
            )}
          </div>
          <div className="small text-muted">
            {[order.booking_reference, operatorName].filter(Boolean).join(' · ')}
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoadingOrder ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="text-muted mt-3">Loading booking details…</p>
          </div>
        ) : (
          <>
            <Error error={error} setError={setError} />
            {stepBody}
          </>
        )}
      </Modal.Body>

      {!isLoadingOrder && <Modal.Footer>{footer}</Modal.Footer>}
    </Modal>
  );
}

export default DriverInputModal;
